import { useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack, Chip,
  Typography, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Button, Avatar, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon, Map as MapIcon,
  CheckCircle as OkIcon, ErrorOutline as OweIcon,
  Groups as ClassIcon, Person as PersonIcon,
  TrendingUp as OverIcon, TrendingDown as OweTrendIcon,
} from '@mui/icons-material';

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmt = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Same effective-balance math the individual map uses — projected to
// "what if approved" so the class picture matches the collector's day.
function projectRow(r) {
  const daily        = Number(r.daily_amount) || 0;
  const amountPaid   = Number(r.amount_paid)  || 0;
  const savedCredit  = Number(r.credit)       || 0;
  const savedArrears = Number(r.arrears)      || 0;
  const creditUsed   = Number(r.credit_used)  || 0;
  const arrearsToday = Number(r.arrears_added)|| 0;
  const status       = String(r.status || '').toLowerCase();

  const chargeToday    = status === 'absent' ? 0 : daily;
  const chargeAfterCr  = Math.max(0, chargeToday - creditUsed);
  const pendingOverflow = Math.max(0, amountPaid - chargeAfterCr);

  let projArrears = savedArrears;
  let projCredit  = savedCredit;
  let overflow    = pendingOverflow;
  if (overflow > 0 && projArrears > 0) {
    const clear = Math.min(overflow, projArrears);
    projArrears -= clear;
    overflow    -= clear;
  }
  projCredit += overflow;
  const daysAhead = daily > 0 ? Math.floor(projCredit / daily) : 0;

  return { daily, amountPaid, status, arrearsToday, pendingOverflow,
           projCredit, projArrears, daysAhead };
}

function paletteKey(tone) {
  switch (tone) {
    case 'money': return 'primary';
    case 'good':  return 'success';
    case 'warn':  return 'warning';
    case 'bad':   return 'error';
    default:      return null;
  }
}

function StatCard({ label, value, tone = 'default', hint }) {
  const key = paletteKey(tone);
  return (
    <Box sx={{
      p: 1, borderRadius: 1.5,
      bgcolor: (theme) => key
        ? alpha(theme.palette[key].main, 0.08)
        : theme.palette.grey[50],
      border: '1px solid',
      borderColor: (theme) => key
        ? alpha(theme.palette[key].main, 0.30)
        : theme.palette.divider,
      minWidth: 0,
    }}>
      <Typography sx={{
        color: key ? `${key}.dark` : 'text.secondary',
        fontWeight: 700, letterSpacing: 0.4,
        textTransform: 'uppercase', fontSize: 9.5, lineHeight: 1.2,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        color: key ? `${key}.dark` : 'text.primary',
        fontWeight: 800, fontSize: 15, lineHeight: 1.15,
        mt: 0.15, ...TNUM,
      }}>
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ color: 'text.secondary', display: 'block',
          mt: 0.15, fontSize: 10, lineHeight: 1.2 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

// Small status pill for the per-student row.
function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  if (s === 'full')    return <Chip size="small" color="success" variant="filled" label="Full" sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#fff' }} />;
  if (s === 'partial') return <Chip size="small" color="warning" variant="outlined" label="Partial" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  if (s === 'none')    return <Chip size="small" color="error" variant="outlined" label="Didn't pay" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  if (s === 'absent')  return <Chip size="small" variant="outlined" label="Absent" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  return <Chip size="small" variant="outlined" label="—" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
}

export default function ClassDistributionMapDialog({
  open, className, incomeType, rows, onClose,
}) {
  // Hooks must run before any early return in this component, so the
  // full-screen check lives here rather than next to the <Dialog>.
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const projections = useMemo(() => (rows || []).map(r => ({ row: r, p: projectRow(r) })), [rows]);

  const totals = useMemo(() => {
    const t = {
      students:      projections.length,
      dailyTotal:    0,
      paidToday:     0,
      pendingOver:   0,
      credit:        0,
      arrears:       0,
      arrearsToday:  0,
      full: 0, partial: 0, none: 0, absent: 0, unmarked: 0,
    };
    for (const { p } of projections) {
      t.dailyTotal   += p.daily;
      t.paidToday    += p.amountPaid;
      t.pendingOver  += p.pendingOverflow;
      t.credit       += p.projCredit;
      t.arrears      += p.projArrears;
      t.arrearsToday += p.arrearsToday;
      if (p.status === 'full')    t.full    += 1;
      else if (p.status === 'partial') t.partial += 1;
      else if (p.status === 'none')    t.none    += 1;
      else if (p.status === 'absent')  t.absent  += 1;
      else t.unmarked += 1;
    }
    return t;
  }, [projections]);

  if (!open) return null;

  // Lead message
  let leadTitle = '';
  let leadColour = 'primary';
  let leadIcon   = <ClassIcon />;
  if (totals.arrearsToday > 0 && totals.pendingOver > 0) {
    leadTitle = `Received GHS ${fmt(totals.paidToday)} today · Advance GHS ${fmt(totals.pendingOver)}, Shortfall GHS ${fmt(totals.arrearsToday)}.`;
    leadColour = 'warning';
    leadIcon = <OweTrendIcon />;
  } else if (totals.pendingOver > 0) {
    leadTitle = `Received GHS ${fmt(totals.paidToday)} today · GHS ${fmt(totals.pendingOver)} in advance credit across the class.`;
    leadColour = 'success';
    leadIcon = <OverIcon />;
  } else if (totals.arrearsToday > 0) {
    leadTitle = `GHS ${fmt(totals.arrearsToday)} was added to class arrears today (unpaid + partial).`;
    leadColour = 'error';
    leadIcon = <OweTrendIcon />;
  } else if (totals.students === 0) {
    leadTitle = 'No students in this class for the selected income type.';
    leadColour = 'primary';
  } else {
    leadTitle = 'Class is settled — no shortfall, no advance credit today.';
    leadColour = 'success';
    leadIcon = <OkIcon />;
  }

  return (
    // Full-screen on a phone — this one is maxWidth="lg", so on a small
    // screen it was a wide table crammed into a narrow window.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      fullScreen={fullScreen}>
      <DialogTitle sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.2, py: 1.3,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 32, height: 32,
          border: '2px solid rgba(255,255,255,0.55)' }}>
          <ClassIcon sx={{ color: '#fff', fontSize: 18 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ lineHeight: 1.1, fontSize: 15, fontWeight: 900 }}>
            {className} · {incomeType}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>
            Class distribution map · today's roll-up
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default', p: 1.5 }}>
        {/* Lead banner */}
        <Box sx={{
          p: 1.1, borderRadius: 1.5, mb: 1.2,
          bgcolor: (t) => alpha(t.palette[leadColour].main, 0.09),
          border: '1px solid',
          borderColor: (t) => alpha(t.palette[leadColour].main, 0.35),
          display: 'flex', alignItems: 'center', gap: 1,
        }}>
          <Box sx={{ color: `${leadColour}.main`, display: 'flex' }}>
            {leadIcon}
          </Box>
          <Typography sx={{ fontWeight: 700, color: `${leadColour}.dark`, fontSize: 13 }}>
            {leadTitle}
          </Typography>
        </Box>

        {/* Class totals */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
          gap: 0.8, mb: 1.2,
        }}>
          <StatCard label="Students" tone="money" value={String(totals.students)}
            hint={
              [`${totals.full} full`,
               `${totals.partial} partial`,
               `${totals.none} didn't pay`,
               `${totals.absent} absent`,
               ...(totals.unmarked > 0 ? [`${totals.unmarked} not yet marked`] : []),
              ].join(' · ')
            } />
          <StatCard label="Daily total" tone="money" value={`GHS ${fmt(totals.dailyTotal)}`}
            hint="Expected if all present" />
          <StatCard label="Paid today" tone={totals.paidToday > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(totals.paidToday)}`} hint="Cash collected" />
          <StatCard label="Advance credit" tone={totals.pendingOver > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(totals.pendingOver)}`} hint="From overpayments" />
          <StatCard label="Total credit" tone={totals.credit > 0 ? 'good' : 'default'}
            value={`GHS ${fmt(totals.credit)}`} hint="Projected across class" />
          <StatCard label="Total arrears" tone={totals.arrears > 0 ? 'bad' : 'default'}
            value={`GHS ${fmt(totals.arrears)}`}
            hint={totals.arrearsToday > 0 ? `+ GHS ${fmt(totals.arrearsToday)} today` : 'Carrying forward'} />
        </Box>

        {/* Per-student roll-up */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 480 }}>
          <Table size="small" stickyHeader sx={{
            '& .MuiTableCell-head': {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
              fontSize: 10, fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: 0.4,
              color: 'primary.dark', py: 0.6,
            },
            '& .MuiTableCell-body': { py: 0.5, fontSize: 11.5 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Student</TableCell>
                <TableCell align="right" sx={{ width: 70 }}>Daily</TableCell>
                <TableCell sx={{ width: 90 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Paid</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Overflow</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Credit</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>Arrears</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Days covered</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projections.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No students to show.
                </TableCell></TableRow>
              )}
              {projections.map(({ row, p }, i) => (
                <TableRow key={row.student_id} sx={{
                  bgcolor: p.projArrears > 0
                    ? (t) => alpha(t.palette.error.main, 0.05)
                    : p.projCredit > 0
                      ? (t) => alpha(t.palette.success.main, 0.05)
                      : 'transparent',
                }}>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Avatar sx={{ width: 22, height: 22,
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        color: 'primary.main' }}>
                        <PersonIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 11.5, lineHeight: 1.15 }}>
                          {row.student_name || '—'}
                        </Typography>
                        <Typography sx={{ ...TNUM, fontSize: 10, color: 'text.secondary' }}>
                          {row.student_id}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM }}>{fmt(p.daily)}</TableCell>
                  <TableCell><StatusPill status={p.status} /></TableCell>
                  <TableCell align="right" sx={{ ...TNUM,
                    color: p.amountPaid > 0 ? 'success.dark' : 'text.disabled',
                    fontWeight: p.amountPaid > 0 ? 700 : 500 }}>
                    {p.amountPaid > 0 ? fmt(p.amountPaid) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM,
                    color: p.pendingOverflow > 0 ? 'success.dark' : 'text.disabled',
                    fontWeight: p.pendingOverflow > 0 ? 700 : 500 }}>
                    {p.pendingOverflow > 0 ? fmt(p.pendingOverflow) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM,
                    color: p.projCredit > 0 ? 'success.dark' : 'text.disabled',
                    fontWeight: p.projCredit > 0 ? 700 : 500 }}>
                    {fmt(p.projCredit)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...TNUM,
                    color: p.projArrears > 0 ? 'error.main' : 'text.disabled',
                    fontWeight: p.projArrears > 0 ? 700 : 500 }}>
                    {fmt(p.projArrears)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {p.daysAhead > 0
                      ? <Chip size="small" color="success" variant="outlined"
                          label={`${p.daysAhead} day${p.daysAhead === 1 ? '' : 's'}`}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                      : p.projArrears > 0
                        ? <Chip size="small" color="error" variant="outlined"
                            icon={<OweIcon sx={{ fontSize: 12 }} />}
                            label="Owing"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                        : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary"
          sx={{ display: 'block', mt: 1, fontStyle: 'italic', fontSize: 10.5 }}>
          Projection assumes today's payments are approved. "Days covered" = how
          many future school days that student's credit buys at their daily rate.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.2, bgcolor: 'background.default' }}>
        <Button onClick={onClose} variant="contained" size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
}
